import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Award, Clapperboard, Calendar, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { PRODUZIONI } from "@/data/content";
import { useLang, pick } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

const SPANS = [
    "md:col-span-7 md:row-span-2 min-h-[300px] md:min-h-0",
    "md:col-span-5 min-h-[280px]",
    "md:col-span-5 min-h-[280px]",
    "md:col-span-6 min-h-[300px] md:min-h-[340px]",
    "md:col-span-6 min-h-[300px] md:min-h-[340px]",
];

function SectionHeading({ label, title, sub, testid }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mb-14 max-w-2xl sm:mb-20"
        >
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-brand sm:text-xs">{label}</p>
            <h2 data-testid={testid} className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                {title}
            </h2>
            {sub && <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">{sub}</p>}
        </motion.div>
    );
}

export { SectionHeading };

export default function Produzioni() {
    const { lang, ui } = useLang();
    const [selected, setSelected] = useState(null);
    const [slide, setSlide] = useState(0);
    const slides = selected ? [selected.img, ...(selected.gallery || [])] : [];

    useEffect(() => {
        setSlide(0);
        if (!selected) return;
        const onKey = (e) => e.key === "Escape" && setSelected(null);
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        window.__lenis?.stop();
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
            window.__lenis?.start();
        };
    }, [selected]);

    return (
        <section id="produzioni" data-testid="produzioni-section" className="relative bg-ink px-5 py-24 sm:px-8 sm:py-32">
            <div className="mx-auto max-w-7xl">
                <SectionHeading
                    label={ui.produzioni.label}
                    title={ui.produzioni.title}
                    sub={ui.produzioni.sub}
                    testid="produzioni-heading"
                />

                <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:auto-rows-[300px]">
                    {PRODUZIONI.map((p, i) => (
                        <motion.button
                            key={p.id}
                            data-testid={`production-card-${p.id}`}
                            initial={{ opacity: 0, y: 44 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
                            onClick={() => setSelected(p)}
                            className={`group relative overflow-hidden rounded-2xl border border-white/10 text-left ${SPANS[i]}`}
                        >
                            <img
                                src={p.cover || p.img}
                                alt={`Still di ${p.titolo}`}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/10 transition-opacity duration-500" />
                            <div className="absolute left-5 top-5 rounded-full border border-white/15 bg-black/50 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand backdrop-blur-md">
                                {pick(lang, p, "categoria")}
                            </div>
                            <div className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/50 text-white opacity-0 backdrop-blur-md transition-all duration-500 group-hover:opacity-100">
                                <ArrowUpRight className="h-4 w-4" />
                            </div>
                            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                                <h3 className="font-display text-2xl font-bold text-white transition-colors duration-300 group-hover:text-brand sm:text-3xl">
                                    {p.titolo}
                                </h3>
                                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                                    {[p.anno, p.cliente].filter(Boolean).join(" · ")}
                                </p>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {selected && (
                    <motion.div className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-6">
                        <motion.div
                            data-testid="production-modal-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelected(null)}
                            className="absolute inset-0 bg-black/85 backdrop-blur-md"
                        />
                        <motion.div
                            data-testid="production-modal"
                            initial={{ opacity: 0, y: 70, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 50, scale: 0.98 }}
                            transition={{ duration: 0.55, ease: EASE }}
                            className="relative z-10 max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-3xl border border-white/10 bg-charcoal sm:rounded-3xl"
                        >
                            <div className="relative h-64 overflow-hidden bg-black sm:h-96" data-testid="production-gallery">
                                <motion.img
                                    key={slides[slide]}
                                    src={slides[slide]}
                                    alt={`${selected.titolo} — ${slide + 1}`}
                                    initial={{ opacity: 0, scale: 1.02 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, ease: EASE }}
                                    className="absolute inset-0 h-full w-full object-contain"
                                />
                                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
                                {slides.length > 1 && (
                                    <>
                                        <button
                                            data-testid="gallery-prev-button"
                                            onClick={() => setSlide((s) => (s - 1 + slides.length) % slides.length)}
                                            className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur-md transition-colors hover:bg-brand"
                                            aria-label={ui.produzioni.prev}
                                        >
                                            <ChevronLeft className="h-5 w-5" />
                                        </button>
                                        <button
                                            data-testid="gallery-next-button"
                                            onClick={() => setSlide((s) => (s + 1) % slides.length)}
                                            className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur-md transition-colors hover:bg-brand"
                                            aria-label={ui.produzioni.next}
                                        >
                                            <ChevronRight className="h-5 w-5" />
                                        </button>
                                        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                                            {slides.map((_, i) => (
                                                <button
                                                    key={i}
                                                    data-testid={`gallery-dot-${i}`}
                                                    onClick={() => setSlide(i)}
                                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                                        i === slide ? "w-6 bg-brand" : "w-1.5 bg-white/40 hover:bg-white/70"
                                                    }`}
                                                    aria-label={`${ui.produzioni.goTo} ${i + 1}`}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                                <button
                                    data-testid="production-modal-close"
                                    onClick={() => setSelected(null)}
                                    className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur-md transition-colors hover:bg-brand"
                                    aria-label={ui.produzioni.close}
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="p-6 sm:p-10">
                                <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                                    <span className="flex items-center gap-1.5 text-brand"><Clapperboard className="h-3.5 w-3.5" />{pick(lang, selected, "categoria")}</span>
                                    {selected.anno && <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{selected.anno}</span>}
                                    {selected.cliente && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{selected.cliente}</span>}
                                </div>
                                <h3 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">{selected.titolo}</h3>
                                <p className="mt-5 text-sm leading-relaxed text-slate-300 sm:text-base">{pick(lang, selected, "descrizione")}</p>

                                <div className="mt-8 border-l-2 border-brand pl-5">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">{ui.produzioni.ruolo}</p>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-300">{pick(lang, selected, "ruolo")}</p>
                                </div>

                                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {pick(lang, selected, "dettagli").map((s) => (
                                        <div key={s.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
                                            <p className="mt-1.5 text-sm font-semibold text-white">{s.value}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 flex items-center gap-3 rounded-xl border border-brand/25 bg-brand/10 p-4">
                                    <Award className="h-5 w-5 shrink-0 text-brand" />
                                    <p className="text-sm font-medium text-white">{pick(lang, selected, "riconoscimento")}</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
